import React, {useCallback, useEffect, useState} from 'react';
import bbox from '@turf/bbox';
import {array, object, string} from 'prop-types';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';

import AreaIndicator from '@app/components/common/AreaIndicator';
import MoreActionsMenu from './FeatureActions/MoreActionsMenu.jsx';
import ReservationsChip from '@components/common/ReservationsChip';
import ZoomToFeatureButton from './FeatureActions/ZoomToFeatureButton.jsx';
import {Box} from '@mui/material';
import {DataGridPremium} from '@mui/x-data-grid-premium';
import {fitExtent} from '@app/actions/map';
import {notify} from '@actions/notifications';
import {trackEvent} from '@app/analytics';
import {useGetFeatures} from '@app/api/features';

const FeatureTable = ({collection, searchString, reservations = []}) => {
  const {t} = useTranslation();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [rowCountState, setRowCountState] = useState(0);
  const dispatch = useDispatch();

  const {data, isLoading} = useGetFeatures(
    {
      collectionId: collection.id,
      params: {
        title: searchString ? `~${searchString}` : undefined,
        limit: paginationModel.pageSize,
        offset: paginationModel.pageSize * paginationModel.page,
      },
    },
    {
      onError: () => {
        dispatch(
          notify({
            message: 'There was an error while loading features',
            severity: 'error',
          })
        );
      },
    }
  );

  useEffect(() => {
    setRowCountState(prevRowCountState =>
      isLoading ? prevRowCountState : data?.numberMatched
    );
  }, [setRowCountState, data?.numberMatched, isLoading]);

  const zoomToFeature = useCallback(
    feature => {
      trackEvent('Features Table', {
        action: 'Zoomed to feature',
        featureId: feature.id,
      });
      dispatch(fitExtent(bbox(feature)));
    },
    [dispatch]
  );

  const columns = [
    {
      field: 'title',
      headerName: 'Feature Name',
      minWidth: 200,
      flex: 1,
      valueGetter: (value, row) => row.properties?.title,
      renderCell: ({value}) => {
        if (value) {
          return <div>{value}</div>;
        } else {
          return (
            <Box
              sx={{
                color: theme => theme.palette.text.secondary,
                fontStyle: 'italic',
              }}
            >
              {t('featureTable.untitledFeature')}
            </Box>
          );
        }
      },
    },
    {
      field: 'area',
      headerName: 'Area',
      flex: 1,
      valueGetter: (value, row) => row.properties['pl:area'],
      renderCell: ({value}) => <AreaIndicator squareMeters={value} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      getActions: ({row}) => [
        <ZoomToFeatureButton
          key={`${row.id}-zoom-to`}
          onClick={() => zoomToFeature(row)}
        />,
        <MoreActionsMenu
          key={`${row.id}-more-actions`}
          feature={row}
          isOwner={collection?.permissions?.is_owner}
        />,
      ],
    },
  ];

  if (reservations.length) {
    const reservationsColumn = {
      field: 'hasReservation',
      headerName: '',
      width: 36,
      minWidth: 36,
      resizable: false,
      align: 'right',
      renderCell: ({row}) => {
        const itemReservations = Array.from(
          new Set(
            reservations
              .filter(r => r.aoi_ref === row.properties['pl:ref'])
              .map(r => r.product_access_id || r.product_id)
          )
        );

        if (itemReservations.length) {
          return (
            <ReservationsChip
              reservationsProductIds={itemReservations}
              showLabel={false}
            />
          );
        }
      },
      cellClassName: 'small-padding--cell',
    };

    columns.splice(0, 0, reservationsColumn);
  }

  return (
    <DataGridPremium
      disableColumnFilter
      disableColumnMenu
      disableColumnReorder
      disableDensitySelector
      disableMultipleColumnsSorting
      disableRowSelectionOnClick
      disableColumnSorting
      rows={data?.features || []}
      columns={columns}
      loading={isLoading}
      paginationMode="server"
      pagination
      rowCount={rowCountState}
      pageSizeOptions={[50]}
      paginationModel={paginationModel}
      onPaginationModelChange={model => setPaginationModel({...model})}
      columnHeaderHeight={40}
      slotProps={{
        loadingOverlay: {
          'data-testid': 'loading-overlay',
        },
      }}
      sx={{
        '--DataGrid-containerBackground': theme => theme.palette.grey[100],
        border: 'none',
        '--DataGrid-rowBorderColor': theme => theme.palette.divider,
        '& .MuiDataGrid-withBorderColor': {
          borderColor: theme => theme.palette.divider,
        },
        '& .small-padding--cell': {
          padding: 0,
          paddingLeft: 0.5,
        },
      }}
    />
  );
};

FeatureTable.propTypes = {
  searchString: string,
  collection: object,
  reservations: array,
};

export default FeatureTable;
